import { ActionIcon, Badge, Button, Stack, Tooltip } from "@mantine/core";
import { GridColDef } from "@mui/x-data-grid";
import { PencilIcon, PlusIcon, TrashIcon } from "@phosphor-icons/react";
import { IconClipboardList } from "@tabler/icons-react";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useMemo } from "react";

import { DataGridBase, useDataGridBaseParams } from "@/components/DataGridBase";
import { DashboardComponents } from "@/features/dashboard/_components/dashboard";
import { MODAL_HANDLER } from "@/lib/Mantine";
import { Driver, useListDriversInfiniteQuery } from "@/store";

export const Route = createFileRoute("/_private/dashboard/_base/motoristas/")({
  component: RouteComponent
});

function RouteComponent() {
  const [query] = useDataGridBaseParams();
  const { data, isFetching, hasNextPage, fetchNextPage, refetch } = useListDriversInfiniteQuery({
    params: {
      filter: query?.quickFilterValues?.join(" ") ?? "",
      pageSize: 50
    }
  });
  const rows = data?.pages.flatMap((page) => page.data) || [];
  const navigate = useNavigate();

  const handleDelete = (id: number) => {
    MODAL_HANDLER.delete.driver({ id });
  };

  const columns: GridColDef<Driver>[] = useMemo(
    () => [
      { field: "id", headerName: "ID" },
      {
        field: "name",
        headerName: "Nome",
        type: "string",
        display: "flex",
        minWidth: 350,
        flex: 1,
        renderCell: ({ row }) => {
          return (
            <div className="flex flex-col">
              <p>
                <strong>Código:</strong> {row.id}
              </p>
              <p>{row.name}</p>
            </div>
          );
        }
      },
      { field: "cpf", headerName: "CPF", type: "string", minWidth: 125 },
      { field: "cnh", headerName: "CNH", type: "string", minWidth: 125 },
      { field: "birthDate", headerName: "Data de Nascimento", type: "string", minWidth: 150, align: "center", headerAlign: "center" },
      {
        field: "isEnabled",
        headerName: "Status",
        minWidth: 100,
        type: "boolean",
        renderCell: ({ value }) => {
          const badgeValues = {
            color: value ? "green" : "red",
            text: value ? "Ativo" : "Inativo"
          };

          return (
            <Badge color={badgeValues.color} variant="light">
              {badgeValues.text}
            </Badge>
          );
        }
      },
      {
        field: "shippingCompany",
        headerName: "Transportadora",
        type: "string",
        minWidth: 200,
        valueGetter: (val, { shippingCompany }) => shippingCompany?.name ?? "Nenhuma"
      },
      {
        field: "company",
        headerName: "Empresa",
        type: "string",
        minWidth: 200,
        valueGetter: (val, { company }) => company?.name ?? "Nenhuma"
      },
      {
        field: "actions",
        headerName: "Ações",
        type: "actions",
        minWidth: 150,
        renderCell: ({ row }) => (
          <ActionIcon.Group>
            <Tooltip label="Editar Motorista">
              <ActionIcon
                color="yellow"
                variant="light"
                onClick={() =>
                  navigate({
                    to: "/dashboard/motoristas/editar/$id/modal",
                    params: { id: row.id },
                    state: { shippingCompany: { id: row.shippingCompany?.id, name: row.shippingCompany?.name } }
                  })
                }
              >
                <PencilIcon />
              </ActionIcon>
            </Tooltip>

            <Tooltip label="Excluir Motorista">
              <ActionIcon color="red" variant="light" onClick={() => handleDelete(row.id)}>
                <TrashIcon />
              </ActionIcon>
            </Tooltip>

            {/* <Tooltip
              label={
                <div className="flex flex-col">
                  <p>Logar como Motorista</p>
                  <p className="text-xs text-gray-300">Acesse a conta do motorista abrindo como guia anônima</p>
                </div>
              }
            >
              <ActionIcon color="green" variant="light" radius="xl">
                <IconLogin2 size={20} stroke={1.5} />
              </ActionIcon>
            </Tooltip> */}

            <Tooltip label="Logs do Motorista">
              <ActionIcon
                color="blue"
                variant="light"
                size="md"
                onClick={() => navigate({ to: "/dashboard/logs/$id/modal", params: { id: row.id }, state: { redirectTo: "/dashboard/motoristas" } })}
              >
                <IconClipboardList size={20} stroke={1.5} />
              </ActionIcon>
            </Tooltip>
          </ActionIcon.Group>
        )
      }
    ],
    []
  );

  return (
    <Stack>
      <div className="flex flex-wrap items-center justify-between">
        <DashboardComponents.Title>Gestão de Motoristas</DashboardComponents.Title>

        <div className="flex flex-1 justify-end gap-2">
          <DashboardComponents.RefreshButton tooltipProps={{ label: "Atualizar Motoristas" }} onClick={refetch} loading={isFetching} />

          <Button onClick={fetchNextPage} disabled={!hasNextPage || isFetching} className="max-md:w-full!">
            Carregar Mais
          </Button>

          <Tooltip label="Cadastrar novo Motorista">
            <Link to="/dashboard/motoristas/cadastrar/modal">
              <ActionIcon variant="filled" size="xl">
                <PlusIcon />
              </ActionIcon>
            </Link>
          </Tooltip>
        </div>
      </div>

      <DataGridBase
        columnVisibilityModel={{
          id: false
        }}
        rows={rows}
        columns={columns}
        loading={isFetching}
      />
    </Stack>
  );
}
